import { useMemo, useState, useEffect } from 'react';
import ActionButtons from '../../../components/common/ActionButtons';
import SimpleModal from '../../../components/common/simple-modal/SimpleModal';
import WellControlConfirmTable from './WellControlConfirmTable';
import './MultiWellControlConfirm.scss';
import '../../../components/common/ActionButtons.scss';
import { useToastService } from '../../../components/toast/ToastService';
import { AssetErrorDetail } from '../../../services/wellControlService';
import { useAppDispatch, useAppSelector } from '../../../hooks/storeHooks';
import {
  markWellScanStarted,
  resetApiCompletedWells,
  resetNodeIdStatusMap,
  resetScanProgressCount,
  setIsTableScanning,
  setIsTableOpen,
  setTotalScanCount,
  setScannedWellsData,
  resetScannedWellsData,
  setApiCompletedWells,
  selectProgressDisplay,
  resetFailedAssets,
  resetTimedOutWells,
} from './WellControlSlice';
import { WELL_CONTROL_ACTION_LABELS } from '../MultiwellControlActions/wellControlActionLabels';

export interface WellData {
  assetId: string;
  wellName: string;
  lastGoodScan: string;
  commsStatus: string;
  command?: string;
}

interface MultiWellControlConfirmProps {
  isOpen: boolean;
  handleCancel: () => void;
  selectedWells: WellData[];
  actionCommand: string;
  executeActionFn: () => Promise<any>;
  onRemoveAsset?: (assetId: string) => void;
}

const MultiWellControlConfirm: React.FC<MultiWellControlConfirmProps> = ({
  isOpen,
  handleCancel,
  selectedWells,
  actionCommand,
  executeActionFn,
  onRemoveAsset,
}) => {
  const dispatch = useAppDispatch();

  const actionLabels = WELL_CONTROL_ACTION_LABELS[actionCommand] || WELL_CONTROL_ACTION_LABELS.Default;

  const { showErrorToast, showInfoToast } = useToastService();
  const [isProcessing, setIsProcessing] = useState(false);
  const [isSuccess, setIsSuccess] = useState(false);
  const [failedAssets, setFailedAssets] = useState<AssetErrorDetail[]>([]);
  const [isScanning, setIsScanning] = useState(false);
  const [scanningProgress, setScanningProgress] = useState(0);
  const [isWebSocketProcessing, setIsWebSocketProcessing] = useState(false);
  const [apiHasResponded, setApiHasResponded] = useState(false);
  const [hasShownEmptyTableToast, setHasShownEmptyTableToast] = useState(false);

  const scannedCount = useAppSelector((state) => state.wellControl.scanProgressCount);
  const apiCompletedWells = useAppSelector((state) => state.wellControl.apiCompletedWells);
  const isTableOpen = useAppSelector((state) => state.wellControl.isTableOpen);
  const isTableScanning = useAppSelector((state) => state.wellControl.isTableScanning);
  const scannedWellsData = useAppSelector((state) => state.wellControl.scannedWellsData);
  const nodeIdStatusMap = useAppSelector((state) => state.wellControl.nodeIdStatusMap);
  const scanState = useAppSelector((state) => state.wellControl);
  const progressDisplay = useAppSelector(selectProgressDisplay);

  const activeWellsData = apiHasResponded && scannedWellsData.length > 0 ? scannedWellsData : selectedWells;
  const isReopenedFromMonitor = isTableOpen && !isOpen;
  const shouldShowModal = isOpen || isReopenedFromMonitor;

  const handleConfirm = () => {
    dispatch(resetScanProgressCount());
    dispatch(resetApiCompletedWells());
    dispatch(resetNodeIdStatusMap());
    dispatch(resetScannedWellsData());
    dispatch(resetFailedAssets());
    dispatch(resetTimedOutWells());

    setIsProcessing(true);
    setIsWebSocketProcessing(true);
    setFailedAssets([]);
    setIsScanning(true);

    dispatch(setIsTableScanning(true));
    dispatch(setIsTableOpen(true));

    selectedWells.forEach((well) => {
      dispatch(markWellScanStarted({ assetId: well.assetId }));
    });

    dispatch(setTotalScanCount(selectedWells.length));

    const wellsDataToStore = selectedWells.map((well) => ({
      ...well,
      command: actionCommand,
    }));
    dispatch(setScannedWellsData(wellsDataToStore));

    executeActionFn()
      .then(() => {
        showInfoToast({ headerMessage: `${actionCommand} command initiated` });
        setApiHasResponded(true);
        setIsSuccess(true);
        setIsScanning(true);
      })
      .catch((error) => {
        // Check if the error contains detailed asset failure information
        if (error?.errorDetails?.assets && error.errorDetails.assets.length > 0) {
          // Show the general error message as a toast
          showErrorToast(error.errorDetails.faultMessage || `${actionCommand} command failed`);
          // Store the failed assets details
          setFailedAssets(error.errorDetails.assets);
        } else {
          showErrorToast(`${actionCommand} command failed`);
        }
      })
      .finally(() => {
        setIsProcessing(false);
      });
  };

  useEffect(() => {
    if (isScanning) {
      setScanningProgress(selectedWells.length > 0 ? (scannedCount / selectedWells.length) * 100 : 0);
    }
  }, [isScanning, scannedCount, selectedWells.length]);

  useEffect(() => {
    if (failedAssets.length > 0 && !isProcessing) {
      setIsWebSocketProcessing(false);
    }
  }, [failedAssets.length, isProcessing]);

  const resetScanState = () => {
    dispatch(setIsTableOpen(false));
    dispatch(setIsTableScanning(false));
    dispatch(resetScanProgressCount());
    dispatch(resetApiCompletedWells());
    dispatch(resetNodeIdStatusMap());
    dispatch(resetScannedWellsData());
    dispatch(resetFailedAssets());
    dispatch(resetTimedOutWells());
  };

  const handleSimpleModalCancel = () => {
    setIsSuccess(false);
    setIsScanning(false);
    setScanningProgress(0);
    setIsWebSocketProcessing(false);
    setApiHasResponded(false);

    resetScanState();
    handleCancel();
  };

  const handleClose = () => {
    dispatch(setIsTableOpen(false));
    if (progressDisplay === 100) {
      resetScanState();
    }
    handleCancel();
  };

  const data = useMemo(() => {
    if (shouldShowModal && activeWellsData.length > 0) {
      return activeWellsData.map((well) => {
        // Check if this asset has an error
        const failedAsset = failedAssets.find((asset) => asset.assetId === well.assetId);
        return {
          ...well,
          command: well.command || actionCommand,
          status: 'Pending',
          hasError: !!failedAsset,
          errorMessage: failedAsset?.faultMessage || '',
        };
      });
    }
    return [];
  }, [shouldShowModal, activeWellsData, actionCommand, failedAssets]);

  useEffect(() => {
    const allDone = data.every((well) => well.status === 'Success' || well.status === 'Failed');
    if (isWebSocketProcessing && allDone) {
      setIsWebSocketProcessing(false);
    }
  }, [data, isWebSocketProcessing]);

  // Close modal when all assets are removed
  useEffect(() => {
    if (data.length === 0 && shouldShowModal && (failedAssets.length > 0 || activeWellsData.length === 0)) {
      handleCancel();
    }
  }, [data.length, shouldShowModal, failedAssets.length, activeWellsData.length, handleCancel]);

  const handleBulkRemoveFailedAssets = () => {
    setIsScanning(false);
    setIsSuccess(false);
    dispatch(resetScanProgressCount());

    if (scannedWellsData.length > 0) {
      const failedIds = failedAssets.map((a) => a.assetId);
      const filteredWells = scannedWellsData.filter((well) => !failedIds.includes(well.assetId));
      dispatch(setScannedWellsData(filteredWells));
      setFailedAssets([]);
    }
  };

  useEffect(() => {
    if (isOpen && !isTableScanning) {
      setIsSuccess(false);
      setFailedAssets([]);
      setIsScanning(false);
      setScanningProgress(0);
      setIsWebSocketProcessing(false);
      setApiHasResponded(false);
      dispatch(resetFailedAssets());
    }
  }, [isOpen, isTableScanning, dispatch]);

  useEffect(() => {
    if (
      shouldShowModal &&
      data.length === 0 &&
      (failedAssets.length > 0 || activeWellsData.length === 0) &&
      !isReopenedFromMonitor
    ) {
      if (!hasShownEmptyTableToast) {
        showErrorToast('Selection Required', 'Please select at least one well to perform this action');
        setHasShownEmptyTableToast(true);
        resetScanState();
      }
    } else if (hasShownEmptyTableToast) {
      setHasShownEmptyTableToast(false);
    }
  }, [
    shouldShowModal,
    data.length,
    failedAssets.length,
    activeWellsData.length,
    showErrorToast,
    hasShownEmptyTableToast,
    isReopenedFromMonitor,
  ]);

  const getScanProgressAndOngoing = (
    totalWells: number,
    nodeIdStatusMap: Record<string, string>,
    apiCompletedWells: Record<string, boolean | null>,
  ) => {
    let completedCount = 0;
    let ongoingCount = 0;
    const webSocketStatuses = Object.values(nodeIdStatusMap);

    if (webSocketStatuses.length > 0 && totalWells > 0) {
      webSocketStatuses.forEach((status) => {
        if (status === 'Success' || status === 'Failed') completedCount++;
        else if (status === 'Processing') ongoingCount++;
      });
    } else {
      completedCount = Object.values(apiCompletedWells).filter((status) => status === true).length;
      ongoingCount = Object.values(apiCompletedWells).filter((status) => status !== true).length;
    }

    const hasOngoingOperations = ongoingCount > 0 || Object.values(apiCompletedWells).some((status) => status !== true);
    const progressPercentage = totalWells > 0 ? (completedCount / totalWells) * 100 : 0;

    return { progressPercentage, hasOngoingOperations };
  };

  useEffect(() => {
    if (!isReopenedFromMonitor) return;

    const hasScanData =
      scannedWellsData.length > 0 ||
      Object.keys(apiCompletedWells).length > 0 ||
      Object.keys(nodeIdStatusMap).length > 0 ||
      isTableScanning;

    if (!hasScanData) {
      console.log('No scan-related data found, not restoring scanning state');
      return;
    }

    setIsScanning(true);
    setIsSuccess(true);
    setApiHasResponded(true);

    const totalWells = scannedWellsData.length > 0 ? scannedWellsData.length : selectedWells.length;
    const { progressPercentage, hasOngoingOperations } = getScanProgressAndOngoing(
      totalWells,
      nodeIdStatusMap,
      apiCompletedWells,
    );

    setIsWebSocketProcessing(hasOngoingOperations);
    setScanningProgress(progressPercentage);
  }, [
    isReopenedFromMonitor,
    scannedWellsData.length,
    apiCompletedWells,
    nodeIdStatusMap,
    isTableScanning,
    selectedWells.length,
  ]);

  function areApiCompletedWellsEqual(a: Record<string, boolean | null>, b: Record<string, boolean | null>) {
    const aKeys = Object.keys(a);
    const bKeys = Object.keys(b);
    if (aKeys.length !== bKeys.length) return false;
    for (const key of aKeys) {
      if (a[key] !== b[key]) return false;
    }
    return true;
  }

  useEffect(() => {
    if (!activeWellsData.length) return;

    const newApiCompletedWells: Record<string, boolean | null> = { ...apiCompletedWells };
    let changed = false;

    activeWellsData.forEach((item) => {
      const assetId = item.assetId;
      const normalizedWellName = item.wellName.trim().toLowerCase();
      const matchedStatus = nodeIdStatusMap[normalizedWellName];
      const timedOut = scanState.timedOutWells[assetId];

      if (matchedStatus === 'Success' && apiCompletedWells[assetId] !== true) {
        newApiCompletedWells[assetId] = true;
        changed = true;
      }
      if (timedOut && apiCompletedWells[assetId] !== false) {
        newApiCompletedWells[assetId] = false;
        changed = true;
      }
    });

    if (
      changed &&
      Object.keys(newApiCompletedWells).length === Object.keys(apiCompletedWells).length &&
      !areApiCompletedWellsEqual(newApiCompletedWells, apiCompletedWells)
    ) {
      dispatch(setApiCompletedWells(newApiCompletedWells));
    }
  }, [activeWellsData, nodeIdStatusMap, scanState.timedOutWells, dispatch]);

  if (!shouldShowModal) {
    return <></>;
  }

  const handleRemoveAsset = (assetId: string) => {
    if (onRemoveAsset) onRemoveAsset(assetId);

    const updatedFailedAssets = failedAssets.filter((a) => a.assetId !== assetId);
    setFailedAssets(updatedFailedAssets);

    const updatedScannedWells = scannedWellsData.filter((well) => well.assetId !== assetId);
    dispatch(setScannedWellsData(updatedScannedWells));

    if (updatedFailedAssets.length === 0) {
      setIsScanning(false);
      setIsSuccess(false);
      dispatch(resetScanProgressCount());
    }
  };

  const allDone = data.length > 0 && data.every((well) => well.status === 'Success' || well.status === 'Failed');

  const showCloseButton = data.length === 0 || isSuccess || isScanning;
  const showCancelButton = failedAssets.length > 0 && !isProcessing;

  let buttonType: 'cancel' | 'close' | 'action' = 'action';
  if (showCancelButton) {
    buttonType = 'cancel';
  } else if (showCloseButton) {
    buttonType = 'close';
  }

  const modalOnClose = buttonType === 'cancel' ? handleSimpleModalCancel : handleClose;

  return (
    <SimpleModal
      disableOutsideClickClose={true}
      onClose={modalOnClose}
      header={`Confirm well control actions`}
      className='well-control-confirm-modal'
    >
      <div style={{ padding: '5px 16px' }}>
        <div className='text-[#b8c5cc] mb-[8px]'>{actionLabels.listMessage}</div>
        <div
          className='overflow-y-auto overflow-x-hidden'
          style={{ maxHeight: failedAssets.length > 0 ? '500px' : '400px' }}
        >
          <WellControlConfirmTable
            data={data}
            name={`${actionCommand}`}
            onRemoveAsset={handleRemoveAsset}
            isSuccess={isSuccess}
            isProcessing={isProcessing}
            showBulkDeleteNotification={failedAssets.length > 0 && !isProcessing}
            onBulkRemoveFailedAssets={handleBulkRemoveFailedAssets}
            showScanningProgressNotification={isScanning}
            totalCount={activeWellsData.length}
            scanningProgress={allDone ? 100 : scanningProgress}
            apiCompletedWells={apiCompletedWells}
            isWebSocketProcessing={isWebSocketProcessing}
          />
        </div>
        {buttonType === 'cancel' ? (
          <div className='action-buttons-container'>
            <div className='buttons-wrapper'>
              <button analytics-id='cancel-button' className='cancel-button' onClick={handleSimpleModalCancel}>
                <span className='button-text'>Cancel</span>
              </button>
            </div>
          </div>
        ) : buttonType === 'close' ? (
          <div className='action-buttons-container'>
            <div className='buttons-wrapper'>
              <button analytics-id='close-button' className='add-task-button' onClick={handleClose}>
                <span className='button-text'>Close</span>
              </button>
            </div>
          </div>
        ) : (
          <ActionButtons
            buttonText={actionLabels.button}
            onSubmit={handleConfirm}
            onCancel={handleSimpleModalCancel}
            isSubmitting={isProcessing}
          />
        )}
      </div>
    </SimpleModal>
  );
};

export default MultiWellControlConfirm;
